import { useEffect } from 'react'
import { db } from '../lib/db'

/**
 * Dev-only keyboard shortcut (⌥⇧⌘R / Ctrl+Alt+Shift+R) to reset app data.
 * Clears auth, IndexedDB, localStorage (preserving theme/language), then restarts.
 *
 * Bis #257 ⇧⌘R — das ist seither „Wiederkehrend“; ein Griff zur Wiederholung hätte in der
 * Entwicklung alle Daten gelöscht. `code` statt `key`: Mit ⌥ meldet der Mac ein Sonderzeichen.
 */
export function useDevReset() {
  useEffect(() => {
    if (import.meta.env.MODE !== 'development') return

    const handleDevReset = async (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.altKey && e.code === 'KeyR') {
        e.preventDefault()

        const isElectron = typeof window !== 'undefined' && 'api' in window

        if (isElectron && (window as any).api?.invoke) {
          try {
            try {
              const { supabase } = await import('../lib/supabase/client')
              if (supabase) await supabase.auth.signOut()
            } catch (_error) {
              // Silent error
            }

            // Hard-clear the OS-encrypted secure storage. signOut() removes the
            // Supabase session via the storage adapter, but it can fail silently
            // (offline / network) — leaving the encrypted token behind and the
            // user still logged in after the reset.
            try {
              await (window as any).api.invoke('safe-storage-clear')
            } catch (err: unknown) {
              console.error('[Dev] Error clearing safe-storage:', err)
            }

            // Must await: the deletion holds the saved name (userProfiles.fullName).
            // Without awaiting, `quit-app` interrupts it and the DB — and the
            // name — survive the reset, re-hydrating on next launch.
            try {
              await db.delete()
            } catch (err: unknown) {
              console.error('[Dev] Error deleting IndexedDB:', err)
            }

            const mode = localStorage.getItem('mode')
            const language = localStorage.getItem('language')
            localStorage.clear()
            sessionStorage.clear()
            if (mode) localStorage.setItem('mode', mode)
            if (language) localStorage.setItem('language', language)
            // KEIN `forceSetupPage` hier. Der Schalter springt in `SetupRoute`
            // noch vor der Willkommens-Bedingung auf die Anmeldeseite — der
            // Reset stellt den Erststart also her und liefe an dessen erstem
            // Bildschirm vorbei. Er stammt aus der Zeit, als die Anmeldeseite
            // DER erste Bildschirm war. Die anderen beiden Setzer
            // (`useConnectCloud`, das Avatar-Menü) wollen die Anmeldeseite zu
            // Recht — dort verbindet ein Offline-Nutzer ein Konto.

            await (window as any).api.invoke('reset-window-settings')
            // Reset main-process prefs (theme/displayMode/sidebars) and wipe the
            // browser session (cookies + HTTP cache) for a true from-scratch start.
            await (window as any).api.invoke('reset-app-preferences')
            await (window as any).api.invoke('clear-web-session')
            await (window as any).api.send('quit-app')
          } catch (error) {
            console.error('[Dev] Error during reset:', error)
          }
        } else {
          try {
            const { supabase } = await import('../lib/supabase/client')
            if (supabase) await supabase.auth.signOut()
          } catch (_error) {
            // Silent error
          }

          try {
            await db.delete()
          } catch (error) {
            console.error('[Dev] Error deleting IndexedDB:', error)
          }

          const mode = localStorage.getItem('mode')
          const language = localStorage.getItem('language')
          localStorage.clear()
          sessionStorage.clear()
          if (mode) localStorage.setItem('mode', mode)
          if (language) localStorage.setItem('language', language)

          window.location.href = '/'
        }
      }
    }

    window.addEventListener('keydown', handleDevReset)
    return () => window.removeEventListener('keydown', handleDevReset)
  }, [])
}
